import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from './icons';
import { BRAND, BRAND_LIGHT, getThemeColors } from '../lib/theme';

export interface ColumnDef {
  id: string;
  label: string;
  /** Spalten mit `required: true` lassen sich nicht abwählen (z.B. der Klient-Name). */
  required?: boolean;
}

interface ColumnPickerProps {
  dark: boolean;
  columns: ColumnDef[];
  visible: string[];
  onChange: (next: string[]) => void;
}

/**
 * Dropdown that lets the user toggle which columns are visible. Order in the
 * dropdown matches `columns`; the resulting `visible` array preserves that
 * order so consumers can simply iterate `columns.filter(c => visible.includes(c.id))`.
 */
export function ColumnPicker({ dark, columns, visible, onChange }: ColumnPickerProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggle = (id: string) => {
    const col = columns.find(c => c.id === id);
    if (col?.required) return;
    const set = new Set(visible);
    set.has(id) ? set.delete(id) : set.add(id);
    // Preserve column order from the definition list:
    onChange(columns.filter(c => set.has(c.id)).map(c => c.id));
  };

  const reset = () => onChange(columns.map(c => c.id));

  const visibleCount = visible.length;
  const totalCount   = columns.length;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '7px 11px', borderRadius: 8,
          border: `1px solid ${t.border}`, background: t.card,
          color: t.text, fontSize: 12, fontWeight: 500, cursor: 'pointer',
          fontFamily: 'Outfit, sans-serif',
        }}
      >
        <Icon.Filter c={t.muted} />
        {tr('common.columns.button', { visible: visibleCount, total: totalCount })}
        <Icon.Chevron dir={open ? 'up' : 'down'} c={t.muted} />
      </button>

      {open && (
        <div
          className="fade-in"
          style={{
            position: 'absolute', right: 0, top: 38, zIndex: 100,
            minWidth: 220,
            background: t.card, border: `1px solid ${t.border}`,
            borderRadius: 10, boxShadow: '0 12px 24px -8px rgba(16,24,40,0.15)',
            padding: 8,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px 8px', borderBottom: `1px solid ${t.borderSoft}`, marginBottom: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: t.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {tr('common.columns.title')}
            </span>
            <button
              onClick={reset}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                fontSize: 11, color: BRAND, fontWeight: 500, padding: 0,
                fontFamily: 'Outfit, sans-serif',
              }}
            >
              {tr('common.columns.all')}
            </button>
          </div>
          {columns.map(c => {
            const checked = visible.includes(c.id);
            const disabled = c.required;
            return (
              <label
                key={c.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '7px 8px', borderRadius: 6,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.6 : 1,
                  background: checked ? BRAND_LIGHT : 'transparent',
                }}
                onMouseEnter={e => { if (!disabled && !checked) (e.currentTarget as HTMLLabelElement).style.background = t.hover; }}
                onMouseLeave={e => { if (!checked) (e.currentTarget as HTMLLabelElement).style.background = 'transparent'; }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => toggle(c.id)}
                  style={{ accentColor: BRAND, margin: 0 }}
                />
                <span style={{ fontSize: 12, color: t.text, fontWeight: 500 }}>{c.label}</span>
                {c.required && (
                  <span style={{ marginLeft: 'auto', fontSize: 10, color: t.muted }}>
                    {tr('common.columns.required')}
                  </span>
                )}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
